import { fillPath, permissionKeyPathParamSchema, route } from "./routes.js";
import { z } from "zod";
import { PERMISSION_KEY_PATTERN } from "@zion/sdk-contracts";
//#region src/role-app-permissions.ts
/**
* `/v1/admin/role-app-permissions/*` — the three per-role per-app grant routes the admin app's role
* detail page calls.
*
* Producer: `AdminRoleAppPermissionGrantsController` and `RoleAppPermissionGrantService` in
* `cloud/dp/core-api/src/admin/`. Consumer: the admin app's `AdminClient`.
*
* Permission keys: the class default is `settings:roles:manage`; the two GETs override it to
* `settings:roles:read`. The org-wide `GET /v1/admin/role-app-permissions` has no page caller and is
* outside this contract; the per-role read below is the one the app uses.
*
* The shell's `RoleAppPermissionGrant` and the controller agree here: five keys, same names, same
* nullability. It is the one family in this set with no response drift.
*
* The revoke route answers 204 with no body, so it has no response schema.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* One `auth.org_role_app_permission_grants` row. The table's primary key is
* `(orgRoleId, appId, permissionSlug)`, so there is no `id` column.
*
* `orgRoleId` is the `auth.org_role_definitions` row's id, not its slug: the route addresses a role
* by slug and the stored row references it by id.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/org-role-app-permission-grants.ts`.
*/
const roleAppPermissionGrantSchema = z.object({
	orgRoleId: z.uuid(),
	appId: z.uuid(),
	permissionSlug: z.string(),
	grantedAt: z.iso.datetime(),
	grantedBy: z.uuid().nullable()
});
/**
* `GET /v1/admin/role-app-permissions/{roleSlug}` — every app-permission grant held by one role.
* Requires `settings:roles:read`.
*/
const LIST_ROLE_APP_PERMISSIONS_FOR_ROLE_ROUTE = route("GET", "/v1/admin/role-app-permissions/{roleSlug}");
/**
* `POST /v1/admin/role-app-permissions` — grant an app permission to a role. Status 201, 409 when
* already granted. Requires `settings:roles:manage`.
*/
const GRANT_ROLE_APP_PERMISSION_ROUTE = route("POST", "/v1/admin/role-app-permissions");
/**
* `DELETE /v1/admin/role-app-permissions/{roleSlug}/{appId}/{permissionSlug}` — revoke one grant.
* Status 204 with no body, 404 when the grant does not exist. Requires `settings:roles:manage`.
*/
const REVOKE_ROLE_APP_PERMISSION_ROUTE = route("DELETE", "/v1/admin/role-app-permissions/{roleSlug}/{appId}/{permissionSlug}");
/** Fill `/v1/admin/role-app-permissions/{roleSlug}`. */
function adminRoleAppPermissionsForRolePath(roleSlug) {
	return fillPath(LIST_ROLE_APP_PERMISSIONS_FOR_ROLE_ROUTE.path, { roleSlug });
}
/**
* Fill `/v1/admin/role-app-permissions/{roleSlug}/{appId}/{permissionSlug}`.
*
* `permissionSlug` is overridden to `permissionKeyPathParamSchema` (R-27). A permission key is
* `<prefix>:<verb>`, and `pathParamSchema` admits only `[A-Za-z0-9._~-]`, so the default would
* refuse every real value this segment carries. The colon is legal `pchar` and travels as `%3A`.
* That schema is `PERMISSION_KEY_PATTERN`'s grammar, so it also refuses `*`, `prefix:*` and every
* other wildcard form.
*
* `roleSlug` and `appId` take the default, so the narrow rule holds everywhere it can.
*/
function adminRoleAppPermissionPath(roleSlug, appId, permissionSlug) {
	return fillPath(REVOKE_ROLE_APP_PERMISSION_ROUTE.path, {
		roleSlug,
		appId,
		permissionSlug
	}, { permissionSlug: permissionKeyPathParamSchema });
}
/** The body of `GET /v1/admin/role-app-permissions/{roleSlug}`. A bare array. */
const listRoleAppPermissionsForRoleResponseSchema = z.array(roleAppPermissionGrantSchema);
/**
* The body of `POST /v1/admin/role-app-permissions`.
*
* `permissionSlug` carries `PERMISSION_KEY_PATTERN`, the anti-privilege-escalation gate: two
* lowercase segments joined by one colon, no wildcards. The grant reaches `orgPermissions` at
* JWT-issue time, so an unvalidated `*` would grant every permission to every member of the role.
* The service repeats the wildcard refusal so a caller that bypasses the pipe is still refused.
*
* The prefix is deliberately not constrained to the app's slug: live declarations carry non-slug
* prefixes such as `docs:write`, and a slug rule here would retro-invalidate granted keys.
*
* `roleSlug` carries the producer's own pattern, which is narrower than the role-create schema's
* bare `z.string().min(1).max(256)`.
*
* Owner: `grantAppPermissionSchema` in `cloud/dp/core-api/src/admin/dto/grant-app-permission.dto.ts`.
*/
const grantRoleAppPermissionRequestSchema = z.strictObject({
	roleSlug: z.string().max(256).regex(/^[a-z][a-z0-9_-]*$/),
	appId: z.uuid(),
	permissionSlug: z.string().max(256).regex(PERMISSION_KEY_PATTERN)
});
/** The body of `POST /v1/admin/role-app-permissions` — status 201, the stored grant row. */
const grantRoleAppPermissionResponseSchema = roleAppPermissionGrantSchema;
/** Every route this file names, for `route-census.ts`. */
const roleAppPermissionsRoutes = Object.freeze([
	LIST_ROLE_APP_PERMISSIONS_FOR_ROLE_ROUTE,
	GRANT_ROLE_APP_PERMISSION_ROUTE,
	REVOKE_ROLE_APP_PERMISSION_ROUTE
]);
//#endregion
export { GRANT_ROLE_APP_PERMISSION_ROUTE, LIST_ROLE_APP_PERMISSIONS_FOR_ROLE_ROUTE, REVOKE_ROLE_APP_PERMISSION_ROUTE, adminRoleAppPermissionPath, adminRoleAppPermissionsForRolePath, grantRoleAppPermissionRequestSchema, grantRoleAppPermissionResponseSchema, listRoleAppPermissionsForRoleResponseSchema, roleAppPermissionGrantSchema, roleAppPermissionsRoutes };

//# sourceMappingURL=role-app-permissions.js.map